import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { isEmpty } from "lodash";
/**
 *
 * A progressive image loading component that optimizes image delivery based on
 * network speed and viewport size to improve performance and user experience.
 *
 * Image Variant Sizes (from Rails ActiveStorage):
 * - thumbnail: 100px width (smallest, fastest to load)
 * - small: 360px width
 * - medium: 720px width
 * - large: 1080px width
 * - src: Original full-resolution image (highest quality)
 *
 * Loading Strategy:
 *
 * 1. Slow 3G Networks (2g, slow-2g):
 *    - Initial load: thumbnail (100px) to minimize data usage
 *    - Progressive upgrade: Uses Intersection Observer to load optimal variant when in view
 *
 * 2. 3G Networks:
 *    - Initial load: thumbnail (100px) as placeholder
 *    - Progressive upgrade: Loads optimal variant based on viewport when in view:
 *      - Viewport ≤ 640px (small): small variant (360px)
 *      - Viewport ≤ 1024px (medium): medium variant (720px)
 *      - Viewport > 1024px: large variant (1080px) - capped to save bandwidth
 *
 * 3. Fast Networks (4G+):
 *    - Initial load: optimal image directly (skips thumbnail to avoid flash)
 *      - Viewport ≤ 640px (small): large variant (1080px) - high quality even on small screens
 *      - Viewport > 640px: original src (full resolution) - best quality for medium+ screens
 *    - No progressive upgrade needed (already optimal)
 *
 * 4. Unknown connection (no navigator.connection, e.g. all iOS browsers):
 *    - Treated as fast for source selection: skips the thumbnail and uses the same
 *      variant selection as fast networks
 *    - The loading placeholder (shimmer + blurred thumbnail) is still shown until the image loads
 *
 * The component uses Intersection Observer for slow/3G networks to progressively upgrade
 * from thumbnail to optimal quality. Fast networks load the optimal image immediately
 * to provide a seamless experience without the thumbnail flash.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import type { ImageVariants } from "./utils";
import {
  getOptimalImageSrc,
  isFastNetwork,
  shouldSkipThumbnail,
} from "./utils";

interface ProgressiveImageProps {
  alt?: string;
  aspectRatio?: string;
  className?: string;
  imageClassName?: string;
  loading?: "lazy" | "eager";
  loadingClassName?: string;
  showShimmer?: boolean;
  src: string;
  srcVariants?: ImageVariants;
}

const INTERSECTION_OBSERVER_OPTIONS = {
  threshold: 0.01,
  rootMargin: "50px",
} as const;

export const ProgressiveImage = ({
  src,
  srcVariants,
  alt = t("post.cover_image_alt"),
  className = "",
  showShimmer = true,
  aspectRatio,
  loading = "lazy",
  imageClassName,
  loadingClassName = "",
}: ProgressiveImageProps) => {
  const getInitialImageSrc = (): string => {
    if (isEmpty(srcVariants)) return src;
    if (shouldSkipThumbnail()) {
      const viewportWidth =
        typeof window !== "undefined" ? window.innerWidth : 1920;
      return getOptimalImageSrc(viewportWidth, srcVariants, src);
    }
    return srcVariants?.thumbnail || src;
  };

  const initialImageSrc = getInitialImageSrc();
  const isFastNetworkConnection = isFastNetwork();

  const [imageSrc, setImageSrc] = useState<string>(initialImageSrc);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [nextImageSrc, setNextImageSrc] = useState<string | null>(null);
  const [isNextImageLoaded, setIsNextImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadedSrcRef = useRef<string>(initialImageSrc);
  const loadingImageRef = useRef<HTMLImageElement | null>(null);
  const isImageVariantsEmpty = isEmpty(srcVariants);
  const shouldShowLoadingState =
    isLoading && isInitialLoad && !hasError && !isFastNetworkConnection;

  const handleImageLoad = () => {
    setIsLoading(false);
    setIsInitialLoad(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
    setImageSrc(src);
  };

  const getOptimalImageSrcForViewport = useCallback(
    (width: number): string => {
      if (isImageVariantsEmpty) return src;
      return getOptimalImageSrc(width, srcVariants, src);
    },
    [isImageVariantsEmpty, src, srcVariants],
  );

  const loadHigherQualityImage = useCallback(
    (targetSrc: string) => {
      if (targetSrc === loadedSrcRef.current || hasError) return;

      if (loadingImageRef.current) {
        loadingImageRef.current.onload = null;
        loadingImageRef.current.onerror = null;
        loadingImageRef.current.src = "";
      }

      const img = new Image();
      loadingImageRef.current = img;
      img.src = targetSrc;

      img.onload = () => {
        if (isInitialLoad) {
          setImageSrc(targetSrc);
          setIsLoading(false);
          setIsInitialLoad(false);
        } else {
          setNextImageSrc(targetSrc);
          setIsNextImageLoaded(false);
        }
        loadedSrcRef.current = targetSrc;
        loadingImageRef.current = null;
      };

      img.onerror = () => {
        setHasError(true);
        setIsLoading(false);
        setImageSrc(src);
        loadingImageRef.current = null;
      };
    },
    [hasError, src, isInitialLoad],
  );

  const handleImageInView = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const viewportWidth = window.innerWidth;
          const optimalSrc = getOptimalImageSrcForViewport(viewportWidth);
          if (optimalSrc !== loadedSrcRef.current) {
            loadHigherQualityImage(optimalSrc);
          }
        }
      }
    },
    [getOptimalImageSrcForViewport, loadHigherQualityImage],
  );

  useEffect(
    () => () => {
      if (loadingImageRef.current) {
        loadingImageRef.current.onload = null;
        loadingImageRef.current.onerror = null;
        loadingImageRef.current.src = "";
      }
    },
    [],
  );

  useEffect(() => {
    if (nextImageSrc && isNextImageLoaded) {
      const timer = setTimeout(() => {
        setImageSrc(nextImageSrc);
        setNextImageSrc(null);
        setIsNextImageLoaded(false);
      }, 50);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [nextImageSrc, isNextImageLoaded]);

  useEffect(() => {
    if (isImageVariantsEmpty || !imageRef.current) return;

    observerRef.current = new IntersectionObserver(
      handleImageInView,
      INTERSECTION_OBSERVER_OPTIONS,
    );

    observerRef.current.observe(imageRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [handleImageInView, isImageVariantsEmpty]);

  if (isImageVariantsEmpty) {
    return (
      <img
        loading={loading}
        src={src}
        alt={alt}
        className={classNames(className, imageClassName)}
      />
    );
  }

  return (
    <div
      className={classNames("relative overflow-hidden", className, {
        [loadingClassName]: isLoading && isInitialLoad && !hasError,
      })}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {shouldShowLoadingState && (
        <>
          <div className="bg-secondary absolute inset-0" />
          {showShimmer && (
            <div className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent bg-[length:1000px_100%] bg-no-repeat" />
          )}
          <img
            loading={loading}
            src={srcVariants?.thumbnail || src}
            alt={alt}
            className={classNames(
              "absolute inset-0 size-full scale-110 blur-md",
              imageClassName,
            )}
            aria-hidden="true"
          />
        </>
      )}

      <img
        loading={loading}
        ref={imageRef}
        src={imageSrc}
        onLoad={handleImageLoad}
        onError={handleImageError}
        alt={alt}
        className={classNames(
          "size-full transition-opacity duration-500",
          imageClassName,
          {
            "opacity-0": isLoading || isNextImageLoaded,
            "opacity-100": !isLoading && !nextImageSrc && !isNextImageLoaded,
          },
        )}
      />
      {nextImageSrc && (
        <img
          loading={loading}
          src={nextImageSrc}
          alt={alt}
          className={classNames(
            "absolute inset-0 size-full transition-opacity duration-500",
            imageClassName,
            {
              "opacity-0": !isNextImageLoaded,
              "opacity-100": isNextImageLoaded,
            },
          )}
          onLoad={() => {
            setIsNextImageLoaded(true);
          }}
        />
      )}
    </div>
  );
};
